/**
 * Research Event Card Component
 * Displays individual research events in the Sources & Resources timeline
 * 
 * Two variants:
 * - Move events (goals, frameworks, plans, workouts)
 * - Chat events (intelligence conversations)
 * 
 * Author: Coach Cedar Development Team
 * Date: October 23, 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, ExternalLink, ChevronDown, ChevronUp, Calendar, Target, Activity, Dumbbell, BookOpen } from 'lucide-react';
import { ContextualChatButton } from './ContextualChatButton';
import '../styles/card-system.css';

// 🆕 DEC 2025: Interface for enriched frameworks
interface EnrichedFramework {
  id: string;
  framework_name: string;
  description: string;
  core_principles?: string[];
  goal_contexts?: string[];
  resource_id: string;
  resource_name: string;
  expertise_areas?: string[];
  consensus_alignment_score?: number;
}

// 🆕 MAR 2026: Interface for enriched expert resources
interface EnrichedExpert {
  id: string;
  name: string;
  credentials?: string[];
  primary_expertise?: string[];
  consensus_alignment_score?: number;
}

interface ResearchSource {
  id: string;
  title: string;
  authors: string | string[];
  journal?: string;
  publication_year?: number;
  doi?: string;
  evidence_strength?: string;
  key_findings?: string[];
  // 🆕 DEC 2025: Primary source fields (from conversation_sources_used)
  application_summary?: string;
  key_influence?: string;
  cited_finding?: string;
  applied_to?: string;
}

interface ResearchEvent {
  id: string;
  user_id: string;
  event_type: string;
  event_title: string;
  event_description: string;
  event_date: string;
  event_date_friendly?: string;

  // Context references
  goal_id?: string;
  plan_id?: string;
  workout_id?: string;
  framework_id?: string;
  insight_id?: string;

  // Intelligence-specific
  intelligence_engine?: string;
  intelligence_engine_label?: string;
  intelligence_subcategory?: string;
  user_query?: string;
  analysis_summary?: string[];

  // Research sources
  research_sources: string[];
  source_count: number;

  // 🆕 DEC 2025: Framework and principle tracking
  resource_frameworks?: string[];
  framework_count?: number;
  consensus_principles?: string[];
  principle_count?: number;

  // 🆕 MAR 2026: Expert resource tracking
  research_resources?: string[];
  resource_count?: number;
}

interface ResearchEventCardProps {
  event: ResearchEvent;
  sources?: ResearchSource[]; // Legacy: enriched_sources (will be deprecated)
  primarySources?: ResearchSource[]; // 🆕 DEC 2025: Sources with application summaries
  supportingSources?: ResearchSource[]; // 🆕 DEC 2025: Supporting context sources
  enrichedFrameworks?: EnrichedFramework[]; // 🆕 DEC 2025: Frameworks used
  enrichedExperts?: EnrichedExpert[]; // 🆕 MAR 2026: Expert resources used
  onChatClick: (eventId: string, event: ResearchEvent) => void;
  initiallyExpanded?: boolean;  // 🔬 NEW (Jan 25, 2026): Auto-expand for deep linking
  eventElementId?: string;  // 🔬 NEW (Jan 25, 2026): Element ID for scroll targeting
}

export const ResearchEventCard: React.FC<ResearchEventCardProps> = ({
  event,
  sources,
  primarySources,
  supportingSources,
  enrichedFrameworks,
  enrichedExperts,
  onChatClick,
  initiallyExpanded = false,
  eventElementId
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [showSources, setShowSources] = useState(false);
  const [showSupporting, setShowSupporting] = useState(false); // 🆕 DEC 2025: Supporting sources toggle
  const navigate = useNavigate();
  
  // 🔬 AUTO-EXPAND: If initially expanded, also show sources
  useEffect(() => {
    if (initiallyExpanded) {
      console.log(`🔬 AUTO-EXPAND: Event ${event.id} set to initially expanded`);
      setExpanded(true);
      setShowSources(true);  // Also show sources section
    }
  }, [initiallyExpanded, event.id]);
  
  // Determine if this is a Move or Chat event
  const isMoveEvent = ['goal_creation', 'framework_generation', 'plan_synthesis', 'workout_card', 'plan_recalibration'].includes(event.event_type);
  const isChatEvent = event.event_type === 'intelligence_conversation';
  
  return (
    <div 
      id={eventElementId}  // 🔬 NEW: Scroll target ID
      className="mypath-card relative" 
      style={{ minHeight: '120px' }}
    >
      {/* HEADER ROW: Date | Topic Pill | Chat Button | Chevron */}
      <div className="flex items-center justify-between px-4 py-3 border-b rounded-t-lg" style={{ 
        backgroundColor: 'var(--colorsurface-50)',
        borderColor: 'var(--cedar-limestone)',
        minHeight: '52px'
      }}>
        {/* Left side: Date + Topic Pill (truncates, never pushes CC button) */}
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
          {/* Date - Format in user's local timezone */}
          <div className="flex items-center gap-1 text-xs font-medium flex-shrink-0" style={{ color: 'var(--coloron-surface600)' }}>
            <Calendar size={12} />
            <span>{event.event_date_friendly || new Date(event.event_date).toLocaleDateString(undefined, { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })}</span>
          </div>
          
          <span className="flex-shrink-0" style={{ color: 'var(--coloron-surface600)' }}>•</span>
          
          {/* Topic Pill - Intelligence or Move type */}
          {isChatEvent && event.intelligence_engine_label && (
            <span className="insights-badge flex-shrink-0">
              {event.intelligence_engine_label}
            </span>
          )}
          
          {isMoveEvent && (
            <span className="insights-badge flex-shrink-0">{formatEventType(event.event_type)}</span>
          )}
        </div>
        
        {/* Right side: CC Chat Button + Expand/Collapse Chevron — always anchored right */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          <ContextualChatButton
            context={{
              type: 'research_event',
              id: event.id,
              title: event.event_title,
              description: event.event_description
            }}
            text="Chat"
            size="small"
            variant="pill"
          />
          <div
            onClick={() => setExpanded(!expanded)}
            className="flex items-center justify-center cursor-pointer rounded-full p-1 transition-colors hover:bg-gray-100"
            style={{ minWidth: '32px', minHeight: '32px', marginTop: '4px', marginRight: '4px' }}
          >
            {expanded ? <ChevronUp size={26} style={{ color: '#1F6C4F' }} /> : <ChevronDown size={26} style={{ color: '#1F6C4F' }} />}
          </div>
        </div>
      </div>
      
      {/* BODY: Title */}
      <div 
        onClick={() => setExpanded(!expanded)}
        className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors" 
        style={{ backgroundColor: '#ffffff', paddingBottom: '6px', position: 'relative', minHeight: '80px' }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {isMoveEvent && <Target size={16} style={{ color: 'var(--cedar-primary)' }} />}
            {isChatEvent && <MessageCircle size={16} style={{ color: 'var(--cedar-primary)' }} />}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-sm" style={{ color: '#1F6C4F' }}>{event.event_title}</h3>
            {isChatEvent && event.intelligence_subcategory && (
              <span className="text-xs capitalize" style={{ color: 'var(--coloron-surface600)' }}>
                {event.intelligence_subcategory}
              </span>
            )}
          </div>
        </div>

        {/* FOOTER ROW: Source + Resource Count (bottom left) */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center gap-2 px-3 py-2 bg-white rounded-b-lg">
          {((event.framework_count || 0) + (event.resource_count || 0)) > 0 && (
            <div className="bg-gray-800 text-white px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1">
              <BookOpen size={12} />
              {(event.framework_count || 0) + (event.resource_count || 0)} resources
            </div>
          )}
          <div className="bg-gray-800 text-white px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1">
            <Activity size={12} />
            {event.source_count} sources
          </div>
        </div>
      </div>
      
      {/* Expanded Content - White background to match card system */}
      {expanded && (
        <div className="px-4 py-3 space-y-4" style={{ backgroundColor: '#ffffff', borderTop: '1px solid var(--cedar-limestone)' }}>
          {/* 1. User Query (for Chat events) - MOVED TO TOP */}
          {isChatEvent && event.user_query && (
            <div className="rounded-lg p-3" style={{ 
              backgroundColor: 'var(--cedar-subtle)',
              border: 'none' 
            }}>
              <p className="text-xs mb-1 font-medium" style={{ color: 'var(--coloron-surface600)' }}>Your Question:</p>
              <p className="text-sm italic" style={{ color: 'var(--coloron-surface900)' }}>"{event.user_query}"</p>
            </div>
          )}
          
          {/* 2. Event Description as "Background" */}
          <div>
            <p className="text-xs font-bold mb-1" style={{ color: 'var(--cedar-dark)' }}>Background:</p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--coloron-surface900)' }}>{event.event_description}</p>
          </div>
          
          {/* 3. WHAT CEDAR ANALYZED - Summary Section - Cedar Subtle inset box */}
          {event.analysis_summary && event.analysis_summary.length > 0 && (
            <div className="rounded-lg p-4" style={{ 
              backgroundColor: 'var(--cedar-subtle)',
              border: 'none'
            }}>
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--cedar-dark)' }}>
                What Cedar Analyzed:
              </h4>
              <ul className="space-y-3">
                {event.analysis_summary.map((point, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        width: '18px',
                        height: '18px',
                        minWidth: '18px',
                        marginTop: '3px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--cedar-primary)',
                        fontSize: '10px',
                        color: '#fff',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >✓</span>
                    <span className="text-sm leading-relaxed" style={{ color: 'var(--coloron-surface900)', flex: 1 }}>
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* 🆕 MAR 2026: RESOURCES SECTION (Frameworks + Experts) — shown FIRST */}
          {((enrichedFrameworks && enrichedFrameworks.length > 0) || (enrichedExperts && enrichedExperts.length > 0)) && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={16} style={{ color: 'var(--cedar-primary)' }} />
                <h4 className="text-sm font-semibold" style={{ color: 'var(--cedar-dark)' }}>
                  Resources ({(enrichedFrameworks?.length || 0) + (enrichedExperts?.length || 0)})
                </h4>
              </div>
              <p className="text-xs mb-3" style={{ color: 'var(--coloron-surface600)' }}>
                Frameworks and experts that guided this recommendation.
              </p>
              <div className="space-y-2">
                {/* Frameworks first */}
                {enrichedFrameworks && enrichedFrameworks.map((fw) => (
                  <FrameworkChip
                    key={fw.id}
                    framework={fw}
                    onResourceClick={() => navigate(`/insights/resource/${fw.resource_id}`)}
                    onChatClick={() => {
                      onChatClick(event.id, event);
                    }}
                  />
                ))}
                {/* Experts second */}
                {enrichedExperts && enrichedExperts.map((expert) => (
                  <ExpertChip
                    key={expert.id}
                    expert={expert}
                    onChatClick={() => {
                      onChatClick(event.id, event);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* SOURCES SECTION — shown after Resources */}
          {(primarySources && primarySources.length > 0) || (supportingSources && supportingSources.length > 0) ? (
            <div className="space-y-4">
              {/* Primary Sources Cited */}
              {primarySources && primarySources.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Activity size={16} style={{ color: 'var(--cedar-primary)' }} />
                    <h4 className="text-sm font-semibold" style={{ color: 'var(--cedar-dark)' }}>
                      Primary Sources Cited ({primarySources.length})
                    </h4>
                  </div>
                  <p className="text-xs mb-3" style={{ color: 'var(--coloron-surface600)' }}>
                    These sources were explicitly cited in Cedar's analysis with specific applications to your situation.
                  </p>
                  <div className="space-y-3">
                    {primarySources.map((source) => (
                      <PrimarySourceCard key={source.id} source={source} />
                    ))}
                  </div>
                </div>
              )}

              {/* Supporting Context Sources */}
              {supportingSources && supportingSources.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowSupporting(!showSupporting)}
                    className="flex items-center gap-2 text-sm font-medium"
                    style={{ color: 'var(--cedar-primary)' }}
                  >
                    <Activity size={14} />
                    {showSupporting ? 'Hide' : 'Show'} Supporting Context ({supportingSources.length} sources)
                  </button>
                  <p className="text-xs mt-1 mb-2" style={{ color: 'var(--coloron-surface600)' }}>
                    These sources informed the overall analysis but weren't explicitly cited.
                  </p>

                  {showSupporting && (
                    <div className="mt-3 space-y-3">
                      {supportingSources.map((source) => (
                        <ResearchSourceCard key={source.id} source={source} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : sources && sources.length > 0 ? (
            // Legacy fallback for old events without two-tier separation
            <div>
              <button
                onClick={() => setShowSources(!showSources)}
                className="flex items-center gap-2 text-sm font-medium"
                style={{ color: 'var(--cedar-primary)' }}
              >
                <Activity size={14} />
                {showSources ? 'Hide' : 'View'} Research Sources ({sources.length})
              </button>

              {showSources && (
                <div className="mt-3 space-y-3">
                  {sources.map((source) => (
                    <ResearchSourceCard key={source.id} source={source} />
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// 🆕 DEC 2025: Primary Source Card Component (with application details)
interface PrimarySourceCardProps {
  source: ResearchSource;
}

const PrimarySourceCard: React.FC<PrimarySourceCardProps> = ({ source }) => {
  const [showFindings, setShowFindings] = useState(false);
  const navigate = useNavigate();
  
  return (
    <div className="rounded-lg p-4" style={{ 
      background: 'linear-gradient(135deg, rgba(66, 165, 165, 0.12), rgba(31, 108, 79, 0.08))',
      border: 'none',
      boxShadow: 'var(--card-drop)'
    }}>
      <div className="space-y-3">
        {/* 1. Title (Full Source Info) */}
        <h4 className="font-semibold text-sm" style={{ color: 'var(--cedar-dark)' }}>{source.title}</h4>
        
        {/* 2. Metadata */}
        <p className="text-xs text-gray-600">
          {Array.isArray(source.authors) ? source.authors.slice(0, 3).join(', ') : source.authors}
          {source.publication_year && ` • ${source.publication_year}`}
          {source.journal && ` • ${source.journal}`}
        </p>
        
        {/* 3. Evidence Strength Badge */}
        {source.evidence_strength && (
          <span className="insights-badge">
            {source.evidence_strength} evidence
          </span>
        )}
        
        {/* 4. Research Finding (Cited Finding + Key Findings) - ABOVE application */}
        {source.cited_finding && (
          <div className="bg-white rounded-md p-3" style={{ border: 'none', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }}>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--cedar-dark)' }}>Research Finding:</p>
            <p className="text-xs italic leading-relaxed" style={{ color: 'var(--coloron-surface900)' }}>"{source.cited_finding}"</p>
          </div>
        )}
        
        {/* Key Findings List */}
        {source.key_findings && source.key_findings.length > 0 && (
          <div className="bg-white rounded-md p-3" style={{ border: 'none', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }}>
            {/* Always show first 2 findings inline */}
            <ul className="space-y-1 mb-2">
              {source.key_findings.slice(0, 2).map((finding, idx) => (
                <li key={idx} className="text-xs leading-relaxed pl-3 relative" style={{ color: 'var(--coloron-surface900)' }}>
                  <span className="absolute left-0" style={{ color: 'var(--cedar-primary)' }}>•</span>
                  {finding}
                </li>
              ))}
            </ul>
            {/* Show "Expand" button if more than 2 findings */}
            {source.key_findings.length > 2 && (
              <div>
                <button
                  onClick={() => setShowFindings(!showFindings)}
                  className="text-xs font-medium"
                  style={{ color: 'var(--cedar-primary)' }}
                >
                  {showFindings ? 'Show less' : `Show ${source.key_findings.length - 2} more findings`}
                </button>
                {showFindings && (
                  <ul className="mt-2 space-y-1">
                    {source.key_findings.slice(2).map((finding, idx) => (
                      <li key={idx} className="text-xs leading-relaxed pl-3 relative" style={{ color: 'var(--coloron-surface900)' }}>
                        <span className="absolute left-0" style={{ color: 'var(--cedar-primary)' }}>•</span>
                        {finding}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* 5. Application Summary - BELOW research findings */}
        {source.application_summary && (
          <div className="rounded-md p-3" style={{ 
            backgroundColor: 'white',
            border: 'none',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--cedar-primary)' }}>How This Research Was Applied:</p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--coloron-surface900)' }}>{source.application_summary}</p>
          </div>
        )}
        
        {/* 6. DOI Link */}
        {source.doi && (
          <a
            href={`https://doi.org/${source.doi}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium"
            style={{ color: 'var(--cedar-primary)' }}
          >
            <ExternalLink size={12} />
            View Full Paper
          </a>
        )}
      </div>
    </div>
  );
};

// Helper: Research Source Card Component
interface ResearchSourceCardProps {
  source: ResearchSource;
}

const ResearchSourceCard: React.FC<ResearchSourceCardProps> = ({ source }) => {
  const [showFindings, setShowFindings] = useState(false);
  
  return (
    <div className="rounded-lg p-3" style={{ 
      backgroundColor: 'var(--cedar-subtle)',
      border: 'none'
    }}>
      <div className="space-y-2">
        <h4 className="font-medium text-sm" style={{ color: 'var(--cedar-dark)' }}>{source.title}</h4>
        <p className="text-xs" style={{ color: 'var(--coloron-surface600)' }}>
          {Array.isArray(source.authors) ? source.authors.join(', ') : source.authors}
          {source.publication_year && ` • ${source.publication_year}`}
          {source.journal && ` • ${source.journal}`}
        </p>
        
        {/* Evidence Strength Badge */}
        {source.evidence_strength && (
          <span className="insights-badge">
            {source.evidence_strength} evidence
          </span>
        )}
        
        {/* Key Findings (Inline with Expand for More) - DEC 2025: TODO #4 */}
        {source.key_findings && source.key_findings.length > 0 && (
          <div>
            {/* Always show first 2 findings inline */}
            <ul className="space-y-1 mb-2">
              {source.key_findings.slice(0, 2).map((finding, idx) => (
                <li key={idx} className="text-xs text-gray-700 pl-3" style={{ borderLeft: 'none' }}>
                  {finding}
                </li>
              ))}
            </ul>
            {/* Show "Expand" button if more than 2 findings */}
            {source.key_findings.length > 2 && (
              <div>
                <button
                  onClick={() => setShowFindings(!showFindings)}
                  className="text-xs font-medium"
                  style={{ color: 'var(--cedar-primary)' }}
                >
                  {showFindings ? 'Show less' : `Show ${source.key_findings.length - 2} more findings`}
                </button>
                {showFindings && (
                  <ul className="mt-2 space-y-1">
                    {source.key_findings.slice(2).map((finding, idx) => (
                      <li key={idx} className="text-xs text-gray-700 pl-3" style={{ borderLeft: 'none' }}>
                        {finding}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* DOI Link */}
        {source.doi && (
          <a
            href={`https://doi.org/${source.doi}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 font-medium"
          >
            <ExternalLink size={12} />
            View Paper
          </a>
        )}
      </div>
    </div>
  );
};

// 🆕 DEC 2025: Framework Chip Component (for Key Resources section)
interface FrameworkChipProps {
  framework: EnrichedFramework;
  onResourceClick: () => void;
  onChatClick: () => void;
}

const FrameworkChip: React.FC<FrameworkChipProps> = ({ framework, onResourceClick, onChatClick }) => {
  const navigate = useNavigate();

  return (
    <div className="rounded-lg p-3" style={{
      background: 'linear-gradient(135deg, rgba(66, 165, 165, 0.12), rgba(31, 108, 79, 0.08))',
      border: 'none',
      boxShadow: 'var(--card-drop)'
    }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--cedar-primary)', color: '#fff' }}>Framework</span>
            <button
              onClick={() => navigate(`/insights/library?tab=resources&filter=frameworks&framework=${framework.id}`)}
              className="font-medium text-sm truncate hover:underline"
              style={{ color: 'var(--cedar-primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
            >
              {framework.framework_name}
            </button>
          </div>
          <p className="text-xs mb-2" style={{ color: 'var(--coloron-surface600)' }}>
            by <button
              onClick={onResourceClick}
              className="font-medium underline"
              style={{ color: 'var(--cedar-primary)' }}
            >
              {framework.resource_name}
            </button>
          </p>
          {framework.description && (
            <p className="text-xs line-clamp-2" style={{ color: 'var(--coloron-surface900)' }}>{framework.description}</p>
          )}

          {/* Consensus Score Badge */}
          {framework.consensus_alignment_score && (
            <div className="mt-2">
              <span className="insights-badge">
                {(framework.consensus_alignment_score * 100).toFixed(0)}% consensus
              </span>
            </div>
          )}
        </div>

        {/* Chat Button */}
        <button
          onClick={onChatClick}
          className="flex-shrink-0 p-2 text-white rounded-lg transition-colors"
          style={{ backgroundColor: 'var(--cedar-primary)' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'}
          title="Chat with Cedar about this"
        >
          <MessageCircle size={14} />
        </button>
      </div>
    </div>
  );
};

// 🆕 MAR 2026: Expert Chip Component (for Resources section)
interface ExpertChipProps {
  expert: EnrichedExpert;
  onChatClick: () => void;
}

const ExpertChip: React.FC<ExpertChipProps> = ({ expert, onChatClick }) => {
  return (
    <div className="rounded-lg p-3" style={{
      background: 'linear-gradient(135deg, rgba(66, 165, 165, 0.12), rgba(31, 108, 79, 0.08))',
      border: 'none',
      boxShadow: 'var(--card-drop)'
    }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#6B7280', color: '#fff' }}>Expert</span>
            <span className="font-medium text-sm" style={{ color: 'var(--cedar-primary)' }}>
              {expert.name}
            </span>
          </div>
          {expert.credentials && expert.credentials.length > 0 && (
            <p className="text-xs mb-1" style={{ color: 'var(--coloron-surface600)' }}>
              {expert.credentials.join(', ')}
            </p>
          )}
          {expert.primary_expertise && expert.primary_expertise.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {expert.primary_expertise.map((area, idx) => (
                <span key={idx} className="insights-badge text-xs">
                  {area}
                </span>
              ))}
            </div>
          )}

          {/* Consensus Score Badge */}
          {expert.consensus_alignment_score && (
            <div className="mt-2">
              <span className="insights-badge">
                {(expert.consensus_alignment_score * 100).toFixed(0)}% consensus
              </span>
            </div>
          )}
        </div>

        {/* Chat Button */}
        <button
          onClick={onChatClick}
          className="flex-shrink-0 p-2 text-white rounded-lg transition-colors"
          style={{ backgroundColor: 'var(--cedar-primary)' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'}
          title="Chat with Cedar about this"
        >
          <MessageCircle size={14} />
        </button>
      </div>
    </div>
  );
};

// Helper: Format event type for display
function formatEventType(eventType: string): string {
  switch (eventType) {
    case 'goal_creation':
      return 'Goal Created';
    case 'framework_generation':
      return 'Framework Generated';
    case 'plan_synthesis':
      return 'Plan Created';
    case 'workout_card':
      return 'Workout Created';
    case 'plan_recalibration':
      return 'Plan Updated';
    default:
      return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
}

